import { ReflectUtils, FunctionUtils, ActionExpression } from "@dooboostore/core";
import { ensureInit, getElementConfig } from "./elementDefine.js";
import { SwcUtils } from "../utils/Utils.js";
import { ConvertUtils } from "@dooboostore/core-web";
const ATTRIBUTE_METADATA_KEY = Symbol.for("simple-web-component:attribute");
const convertValue = (val, type) => {
  if (val === null || val === void 0) return val;
  if (type === Number) return Number(val);
  if (type === Boolean) return typeof val === "string" ? !(val === "false" || val === "0") : Boolean(val);
  return val;
};
const getAttributeValue = (inst, attrName, options) => {
  const currentWin = SwcUtils.resolveWindow(inst);
  const targets = options?.selector ? resolveAttributeTargets(inst, options.selector, options) : [inst];
  const primaryTarget = targets[0];
  if (primaryTarget && typeof primaryTarget.hasAttribute === "function" && primaryTarget.hasAttribute(attrName)) {
    let domVal = primaryTarget.getAttribute(attrName);
    const ae = new ActionExpression(domVal);
    const expr = ae.getFirstExpression("callReturn");
    if (expr) {
      try {
        const result = FunctionUtils.executeReturn({
          script: ConvertUtils.decodeHtmlEntity(expr.script, currentWin.document),
          context: inst,
          args: SwcUtils.getHelperAndHostSet(inst)
        });
        return convertValue(result, options?.type);
      } catch (err) {
        console.error(`[SWC] Failed to execute directive {{= ${expr.script} }}:`, err);
        return convertValue(domVal, options?.type);
      }
    }
    return convertValue(domVal, options?.type);
  }
  return null;
};
const resolveAttributeTargets = (inst, selector, options) => {
  const conf = getElementConfig(inst);
  const currentWin = conf.window;
  const r = options.root || "auto";
  const results = [];
  let resolvedSelector = selector;
  if (typeof selector === "function") {
    const hostSet = SwcUtils.getHelperAndHostSet(inst);
    resolvedSelector = selector(inst, hostSet);
  }
  if (resolvedSelector instanceof currentWin.Element) {
    results.push(resolvedSelector);
    return results;
  }
  if (resolvedSelector instanceof currentWin.NodeList) {
    results.push(...Array.from(resolvedSelector));
    return results;
  }
  if (Array.isArray(resolvedSelector)) {
    results.push(...resolvedSelector);
    return results;
  }
  if (resolvedSelector === null) {
    return results;
  }
  const stringSelector = resolvedSelector;
  const applyRoot = (target) => {
    if (!target) return;
    if (r === "auto") {
      results.push(target);
    } else {
      if (r === "light" || r === "all") results.push(target);
      if ((r === "shadow" || r === "all") && target.shadowRoot) results.push(target.shadowRoot);
    }
  };
  if (stringSelector === "$this" || !stringSelector) {
    applyRoot(inst);
  } else {
    const hostSet = SwcUtils.getHostSet(inst);
    if (stringSelector === "$host") applyRoot(hostSet.$host);
    else if (stringSelector === "$parentHost") applyRoot(hostSet.$parentHost);
    else if (stringSelector === "$appHost") applyRoot(hostSet.$appHost);
    else if (stringSelector === "$firstHost") applyRoot(hostSet.$firstHost);
    else if (stringSelector === "$lastHost") applyRoot(hostSet.$lastHost);
    else if (stringSelector === "$firstAppHost") applyRoot(hostSet.$firstAppHost);
    else if (stringSelector === "$lastAppHost") applyRoot(hostSet.$lastAppHost);
    else if (stringSelector === "$hosts") hostSet.$hosts.forEach(applyRoot);
    else if (stringSelector === "$appHosts") hostSet.$appHosts.forEach(applyRoot);
    else {
      if (r === "shadow") {
        const found = inst.shadowRoot?.querySelectorAll(stringSelector);
        if (found) results.push(...found);
      } else if (r === "light") {
        const found = inst.querySelectorAll(stringSelector);
        if (found) results.push(...found);
      } else if (r === "all") {
        const sMatch = inst.shadowRoot?.querySelectorAll(stringSelector);
        const lMatch = inst.querySelectorAll(stringSelector);
        if (sMatch) results.push(...sMatch);
        if (lMatch) results.push(...lMatch);
      } else {
        const found = (inst.shadowRoot || inst).querySelectorAll(stringSelector);
        if (found) results.push(...found);
      }
    }
  }
  return results;
};
function applyAttribute(selector, targetAttributeNameOrOptions, options) {
  return (target, propertyKey, descriptor) => {
    const constructor = target.constructor;
    const designType = Reflect.getMetadata("design:type", target, propertyKey);
    const metaType = descriptor && typeof descriptor.value === "function" ? "method" : "property";
    let finalOptions = {};
    let targetAttributeName;
    if (typeof targetAttributeNameOrOptions === "string") {
      targetAttributeName = targetAttributeNameOrOptions;
      finalOptions = options || {};
    } else if (targetAttributeNameOrOptions) {
      finalOptions = targetAttributeNameOrOptions;
      targetAttributeName = finalOptions.name || String(propertyKey);
    } else {
      targetAttributeName = String(propertyKey);
    }
    let metaList = ReflectUtils.getOwnMetadata(ATTRIBUTE_METADATA_KEY, constructor);
    if (!metaList) {
      metaList = [];
      ReflectUtils.defineMetadata(ATTRIBUTE_METADATA_KEY, metaList, constructor);
    }
    metaList.push({
      propertyKey,
      selector,
      targetAttributeName,
      options: finalOptions,
      type: metaType
    });
    const applyValueToTargets = (inst, resolvedValue) => {
      const targetEls = resolveAttributeTargets(inst, selector, finalOptions);
      const hostSet = SwcUtils.getHelperAndHostSet(inst);
      targetEls.forEach((targetEl) => {
        if (finalOptions.filter && !finalOptions.filter(targetEl, resolvedValue, { currentThis: inst, helper: hostSet })) {
          return;
        }
        if (resolvedValue === null) {
          targetEl.removeAttribute(targetAttributeName);
        } else if (resolvedValue !== void 0) {
          targetEl.setAttribute(targetAttributeName, resolvedValue === true ? "" : String(resolvedValue));
        }
      });
      return resolvedValue;
    };
    if (descriptor && typeof descriptor.value === "function") {
      const original = descriptor.value;
      descriptor.value = function(...args) {
        ensureInit(this);
        const res = original.apply(this, args);
        const extractValue = (v) => {
          const keyToUse = finalOptions.valueKey ?? ATTRIBUTE_METADATA_KEY;
          if (v && typeof v === "object" && keyToUse in v) {
            return v[keyToUse];
          }
          return v;
        };
        if (res instanceof Promise) {
          return res.then((v) => {
            const extracted = extractValue(v);
            applyValueToTargets(this, extracted);
            return v;
          });
        } else {
          const extracted = extractValue(res);
          applyValueToTargets(this, extracted);
          return res;
        }
      };
      return;
    }
    Object.defineProperty(target, propertyKey, {
      configurable: true,
      enumerable: true,
      get() {
        ensureInit(this);
        const mergedOptions = { ...finalOptions, selector, type: finalOptions.type || designType };
        return getAttributeValue(this, targetAttributeName, mergedOptions);
      },
      set(value) {
        ensureInit(this);
        if (value instanceof Promise) {
          value.then((v) => applyValueToTargets(this, v)).catch(() => {
          });
        } else {
          applyValueToTargets(this, value);
        }
      }
    });
  };
}
function attribute(selectorOrAttributeOrTarget, attributeNameOrOptions, optionsOrDescriptor) {
  if (typeof selectorOrAttributeOrTarget === "string") {
    if (typeof attributeNameOrOptions !== "string") {
      return applyAttribute("$this", selectorOrAttributeOrTarget, attributeNameOrOptions);
    }
    return applyAttribute(selectorOrAttributeOrTarget, attributeNameOrOptions, optionsOrDescriptor);
  }
  if (typeof selectorOrAttributeOrTarget === "function") {
    return applyAttribute(selectorOrAttributeOrTarget, attributeNameOrOptions, optionsOrDescriptor);
  }
  if (selectorOrAttributeOrTarget && (typeof attributeNameOrOptions === "string" || typeof attributeNameOrOptions === "symbol")) {
    return applyAttribute("$this", {})(selectorOrAttributeOrTarget, attributeNameOrOptions, optionsOrDescriptor);
  }
  return applyAttribute("$this", selectorOrAttributeOrTarget);
}
function removeAttribute(selector, attributeName, options) {
  return (target, propertyKey, descriptor) => {
    const original = descriptor.value;
    descriptor.value = function(...args) {
      const res = original.apply(this, args);
      const doRemove = (v) => {
        const vk = options?.valueKey;
        const go = vk !== void 0 ? v && typeof v === "object" ? v[vk] : void 0 : true;
        if (!go) return;
        resolveAttributeTargets(this, selector, options ?? {}).forEach((el) => el.removeAttribute(attributeName));
      };
      if (res instanceof Promise) {
        return res.then((v) => {
          doRemove(v);
          return v;
        });
      }
      doRemove(res);
      return res;
    };
    return descriptor;
  };
}
const findAllAttributeMetadata = (target) => {
  const actualTarget = typeof target === "function" ? target : target.constructor;
  return ReflectUtils.findAllMetadata(ATTRIBUTE_METADATA_KEY, actualTarget).flat();
};
const findAllAttributeApplyMetadata = (target) => {
  const result = /* @__PURE__ */ new Map();
  findAllAttributeMetadata(target).forEach((meta) => {
    result.set(meta.propertyKey, meta);
  });
  return result;
};
const attr = applyAttribute;
function attrAppHost(targetAttributeNameOrTarget, optionsOrPropertyKey, descriptor) {
  if ((typeof optionsOrPropertyKey === "string" || typeof optionsOrPropertyKey === "symbol") && descriptor !== void 0) {
    return applyAttribute("$appHost", void 0, {})(targetAttributeNameOrTarget, optionsOrPropertyKey, descriptor);
  }
  return applyAttribute("$appHost", targetAttributeNameOrTarget, optionsOrPropertyKey);
}
function attrLight(selector, targetAttributeName, options) {
  return applyAttribute(selector, targetAttributeName, { ...options ?? {}, root: "light" });
}
function attrShadow(selector, targetAttributeName, options) {
  return applyAttribute(selector, targetAttributeName, { ...options ?? {}, root: "shadow" });
}
function attrAll(selector, targetAttributeName, options) {
  return applyAttribute(selector, targetAttributeName, { ...options ?? {}, root: "all" });
}
var applyAttribute_default = applyAttribute;
export {
  ATTRIBUTE_METADATA_KEY,
  applyAttribute,
  attr,
  attrAll,
  attrAppHost,
  attrLight,
  attrShadow,
  attribute,
  convertValue,
  applyAttribute_default as default,
  findAllAttributeApplyMetadata,
  findAllAttributeMetadata,
  getAttributeValue,
  removeAttribute,
  resolveAttributeTargets
};
//# sourceMappingURL=applyAttribute.js.map
